export type Language = "en" | "es" | "it" | "pt" | "tr" | "fr" | "de";

export type LanguageInfo = {
  code: Language;
  label: string;
  nativeName: string;
};

export const LANGUAGES: LanguageInfo[] = [
  { code: "en", label: "EN", nativeName: "English" },
  { code: "es", label: "ES", nativeName: "Español" },
  { code: "it", label: "IT", nativeName: "Italiano" },
  { code: "pt", label: "PT", nativeName: "Português" },
  { code: "tr", label: "TR", nativeName: "Türkçe" },
  { code: "fr", label: "FR", nativeName: "Français" },
  { code: "de", label: "DE", nativeName: "Deutsch" },
];

export type TranslationItem = {
  title: string;
  description: string;
};

export type FaqItem = {
  question: string;
  answer: string;
};

export type Translations = {
  nav: {
    features: string;
    pricing: string;
    blog: string;
    freeTools: string;
    contact: string;
    startFree: string;
    solutions: string;
    useCases: string;
    switchToDark: string;
    switchToLight: string;
    solutionsToolsDesc: string;
    solutionsUseCasesDesc: string;
  };
  hero: {
    rotatingWords: string[];
    headline: string;
    subheadline: string;
    cta: string;
    helperText: string;
    helperSeparator: string;
    trustedBy: string;
    creators: string;
    dmsSent: string;
    dmsCount: string;
  };
  features: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    cta: string;
    items: TranslationItem[];
  };
  process: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    step: string;
    cta: string;
    steps: TranslationItem[];
  };
  engagement: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    card1Title: string;
    card1TitleLine2: string;
    card1Description: string;
    card2Title: string;
    card2Description: string;
  };
  graph: {
    headingHighlight: string;
    headingHighlightWord: string;
    headingDescription: string;
    title: string;
    by10x: string;
    subtitle: string;
    description: string;
    afterZapify: string;
    cta: string;
  };
  testimonials: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    followers: string;
  };
  faq: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    items: FaqItem[];
  };
  verified: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    cta: string;
    badge: string;
    heading: string;
    description: string;
    missionNote: string;
    close: string;
  };
  stats: {
    dmsSent: string;
    followersGained: string;
    commentsSent: string;
    countries: string;
  };
  pricing: {
    headingTitle: string;
    headingHighlight: string;
    headingDescription: string;
    monthly: string;
    yearly: string;
    get2MonthsFree: string;
    freeTrial: string;
    freeTrialDesc: string;
    starter: string;
    starterDesc: string;
    professional: string;
    professionalDesc: string;
    subscribe: string;
    startFreeTrial: string;
    free: string;
    noCreditCard: string;
    bestSeller: string;
    perMonth: string;
    save: string;
    unlimited: string;
    limited: string;
    email: string;
    priority: string;
    automations: string;
    dms: string;
    aiReplies: string;
    askToFollow: string;
    emailCollection: string;
    igProfile: string;
    teamAccess: string;
    support: string;
    // Category headers
    automationPower: string;
    aiCapabilities: string;
    leadGeneration: string;
    analyticsTeam: string;
    supportPlatform: string;
    soonBadge: string;
    // AUTOMATION POWER features
    activeAutomations: string;
    instagramAccounts: string;
    customTriggerWords: string;
    instantDMReplies: string;
    commentToDMAutomation: string;
    storyReplyAutomation: string;
    welcomeMessageAutomation: string;
    smartGreetingMessage: string;
    unlimitedPostsStories: string;
    flexiblePostTargeting: string;
    linkInDMCTAButtons: string;
    followGatedDMsLinks: string;
    instagramSafetyLimits: string;
    customFlowBuilder: string;
    // AI CAPABILITIES features
    aiCommentReplies: string;
    aiPoweredInstantReplies: string;
    aiTrainedOnBrand: string;
    // LEAD GENERATION features
    storedContacts: string;
    emailCaptureInsideDMs: string;
    // ANALYTICS & TEAM features
    instagramInsights: string;
    monthlyPerformanceReports: string;
    teamMembers: string;
    // SUPPORT & PLATFORM features
    customerSupport: string;
    languageSupport: string;
    platformBranding: string;
    offerTitle: string;
    offerDesc: string;
    offerBadge: string;
    limitedSpots: string;
    offerEndsIn: string;
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
    claimed: string;
    secureCheckout: string;
    noCreditCardRequired: string;
    copied: string;
    comparePlansEyebrow: string;
    comparePlansTitle: string;
    comparePlansSubtitle: string;
    compareFeatureColumn: string;
    plansCtaBadgeLead: string;
    plansCtaBadgeEmphasis: string;
    plansCtaTitleLead: string;
    plansCtaTitleGradient: string;
    plansCtaSubtitle: string;
    plansCtaSubtitleParagraph2: string;
    plansCtaBullet1: string;
    plansCtaBullet3: string;
    plansCtaTrust: string;
    signupMixEyebrow: string;
    signupMixCountLabel: string;
    signupMixLeadTitle: string;
    signupMixLeadSubtitle: string;
    signupMixLabelFreeTrial: string;
    signupMixLabelStarter: string;
    signupMixLabelProfessional: string;
  };
  cta: {
    readyToTransform: string;
    joinThousands: string;
    startForFree: string;
  };
  sticky: {
    instantlyDm: string;
    limitedOffer: string;
    endsIn: string;
    getStartedFree: string;
    start: string;
  };
  contactPage: {
    heading: string;
    subtitle: string;
    connectWithUs: string;
    connectDescription: string;
    emailUs: string;
    emailResponse: string;
    support: string;
    getInstantHelp: string;
    scheduleMeet: string;
    followUs: string;
    stayUpdated: string;
    responseTime: string;
  };
  contactForm: {
    heading: string;
    description: string;
    fullName: string;
    fullNamePlaceholder: string;
    emailAddress: string;
    emailPlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    message: string;
    messagePlaceholder: string;
    sending: string;
    sendMessage: string;
    successMessage: string;
    errorMessage: string;
  };
  blog: {
    insightsStrategy: string;
    masterTheArt: string;
    instagramGrowth: string;
    blogSubtitle: string;
    searchPlaceholder: string;
    followForUpdates: string;
    latestStories: string;
    minRead: string;
    readStory: string;
    featuredStory: string;
    freshDrop: string;
    readArticle: string;
    noArticlesFound: string;
    noArticlesDescription: string;
    clearSearch: string;
  };
  referral: {
    heading: string;
    headingHighlight: string;
    description: string;
    proSubscriber: string;
    minimumDms: string;
    joinProgram: string;
    termsOfService: string;
    step1: string;
    step1Title: string;
    step1Desc: string;
    referralLink: string;
    copy: string;
    step2: string;
    step2Title: string;
    step2Desc: string;
    step3: string;
    step3Title: string;
    step3Desc: string;
    quickStats: string;
    clicks: string;
    converted: string;
    total: string;
    pending: string;
    faqHeading: string;
    faqSubheading: string;
    faq1Q: string;
    faq1A: string;
    faq2Q: string;
    faq2A: string;
    faq3Q: string;
    faq3A: string;
    faq4Q: string;
    faq4A: string;
  };
  toolsPage: {
    heading: string;
    headingHighlight: string;
    description: string;
    searchPlaceholder: string;
    all: string;
    noToolsFound: string;
    free: string;
    comingSoon: string;
    relatedTools: string;
    relatedToolsDesc: string;
    viewAllTools: string;
    frequentlyAsked: string;
    questionsHighlight: string;
    faqDescription: string;
  };
  useCase: {
    heading: string;
    headingHighlight: string;
    description: string;
    startForFree: string;
    learnMore: string;
    bottomCtaHeading: string;
    bottomCtaDesc: string;
    freeToStart: string;
  };
  footer: {
    description: string;
    product: string;
    company: string;
    compare: string;
    howItWorks: string;
    termsOfService: string;
    privacyPolicy: string;
    refundPolicy: string;
    contactUs: string;
    referralProgram: string;
    copyright: string;
    builtWith: string;
    useCasesHeading: string;
    allUseCases: string;
    sendLinksInDms: string;
    autoRespondComments: string;
    collectEmails: string;
    storyReplies: string;
  };
  auth: {
    welcomeToZapify: string;
    setItUpOnce: string;
    recommended: string;
    continueWithInstagram: string;
    continueWithGoogle: string;
    redirecting: string;
    orContinueWithEmail: string;
    emailAddress: string;
    emailRequired: string;
    validEmail: string;
    sendingCode: string;
    startFree: string;
    enterVerificationCode: string;
    weSentCode: string;
    otpRequired: string;
    otpDigits: string;
    verifying: string;
    verifyAndContinue: string;
    useDifferentEmail: string;
    agreeToTerms: string;
    termsOfService: string;
    and: string;
    privacyPolicy: string;
    haveDiscountCode: string;
    discountCode: string;
    enterCode: string;
    apply: string;
    applied: string;
    congratsDiscount: string;
    invalidCode: string;
    redirectingToDashboard: string;
    referralBannerText: string;
    extendedTrialBannerText: string;
  };
  comparison: {
    updatedDate: string;
    startFreeNoCreditCard: string;
    seeFullComparison: string;
    quickAnswer: string;
    featureComparison: string;
    samePriceTitle: string;
    aiVsKeywords: string;
    userComments: string;
    bottomCtaHeading: string;
    bottomCtaDesc: string;
    getStarted: string;
    freeToStart: string;
    feature: string;
    category: string;
    pricingTiers: string;
    faq: string;
  };
  legal: {
    lastUpdated: string;
    haveQuestions: string;
    contactEmail: string;
    returnToWebsite: string;
    termsOfService: string;
    privacyPolicy: string;
    refundPolicy: string;
    dataDeletion: string;
  };
  useCaseLayout: {
    startForFree: string;
    freeToStart: string;
    frequentlyAsked: string;
    questionsHighlight: string;
    readyToAutomate: string;
    readyToAutomateDesc: string;
    trigger: string;
    response: string;
    exploreMore: string;
  };
};
